import SiteService from "../../../services/SiteService";
import { useEffect, useRef, useState } from "react";
import NextIcon from "../../../svg/NextIcon";
import ImagePopout from "../../ui/ImagePopout";
import useSwipe from "../../../hooks/useSwipe";

export default function ArticleImageCarousel({ part, article, images, useOriginalValueAsSrc }) {
	const [isMobile, setIsMobile] = useState(false);
	let [selectedIndex, setSelectedIndex] = useState(0);
	const imagesRef = useRef();
	const carouselContainerRef = useRef();
	const currentImageRef = useRef(null);

	// cPart derived from part or images (whichever is available)
	const [cPart, setCPart] = useState({ images: [] });

	// Handle responsiveness
	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth < 810);
		};

		handleResize(); // Initial check
		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	// Handle setting cPart based on part or images
	useEffect(() => {
		if (images && images.length > 0) {
			setCPart({ images: [...images] });
		} else if (part && part.images && part.images.length > 0) {
			setCPart(part);
		}
	}, [part, images]);

	// Handle the selectedIndex reset if cPart images length changes
	useEffect(() => {
		if (selectedIndex >= cPart.images.length) {
			setSelectedIndex(0);
		}
	}, [cPart.images]);

	// Carousel navigation logic
	const changeIndex = (diff) => {
		let newIndex = selectedIndex + diff;
		if (newIndex >= cPart.images.length) {
			newIndex = 0;
		} else if (newIndex < 0) {
			newIndex = cPart.images.length - 1;
		}
		setSelectedIndex(newIndex);

		if (!isMobile) {
			imagesRef.current.childNodes[newIndex].scrollIntoView({
				behavior: 'smooth',
				block: 'nearest',
				inline: 'center',
			});
		}
	};

	if (!cPart.images || cPart.images.length === 0) {
		return null; // Don't render the carousel if there are no images
	}

	// Build the image source URL
	let baseUrl = null;
	if (article && article._id) {
		baseUrl = `${SiteService.apiPath}/image/articles/${article._id}/`;
	}

	if (!cPart.images[selectedIndex]){
		selectedIndex = 0;
		setSelectedIndex(0);
	}
	const currentImageSrc = (
		(cPart.images&&cPart.images[selectedIndex] && cPart.images[selectedIndex].value) && cPart.images[selectedIndex].value.startsWith('data:image'))
		? cPart.images[selectedIndex].value
		: baseUrl + cPart.images[selectedIndex].value

	const getAspectRatio = (img) => {
		try {
			if (imagesRef.current) {
				img = imagesRef.current.childNodes[selectedIndex];
			}
		} catch (e) {
			console.error(e);
		}

		if (!img) return '';

		const ratio = img.naturalHeight / img.naturalWidth;
		return ratio > 1 ? (isMobile ? 'portrait' : 'landscape') : (isMobile ? 'landscape' : 'portrait');
	};

	//useSwipe(carouselContainerRef, null, null, () => changeIndex(1), () => changeIndex(-1));

	return (
		<div
			ref={carouselContainerRef}
			tabIndex={0}
			className={['carouselContainer' + cPart.variant, 'carouselContainer'].join(' ')}
			onKeyDown={(ev) => {
				if (ev.key === 'ArrowRight') {
					changeIndex(1);
				} else if (ev.key === 'ArrowLeft') {
					changeIndex(-1);
				}
			}}
		>
			<div className="carouselDisplayedImageContainer">
				<img
					loading="lazy"
					style={{ objectFit: getAspectRatio() === 'landscape' ? 'contain' : 'cover' }}
					src={useOriginalValueAsSrc ? cPart.images[selectedIndex].value : currentImageSrc}
					onLoad={(ev) => {
						ev.currentTarget.style.objectFit = getAspectRatio() === 'landscape' ? 'contain' : 'cover';
					}}
					alt={cPart.images[selectedIndex].alt}
					ref={currentImageRef}
				/>
				{cPart.images.length>1&&<><button className="carouselArrow carouselArrowLeft" onClick={() => changeIndex(-1)}>
					<NextIcon/>
				</button>
					<button className="carouselArrow carouselArrowRight" onClick={() => changeIndex(1)}>
				<NextIcon/>
				</button></>}
				{!isMobile && cPart.images[selectedIndex].desc && (
					<div className="carouselDesc">
						<span>{cPart.images[selectedIndex].desc}</span>
					</div>
				)}
			</div>
			{isMobile && cPart.images[selectedIndex].desc && (
				<div className="carouselDesc">
					<span>{cPart.images[selectedIndex].desc}</span>
				</div>
			)}
			{cPart.images.length>1&&<div ref={imagesRef} className="carouselImages">
				{cPart.images.map((carouselPart, i) => {
					const src = carouselPart.value.startsWith('data:image') || carouselPart.value.startsWith('http')
						? carouselPart.value
						: baseUrl + carouselPart.value;

					return (
						<img
							loading="lazy"
							key={i}
							draggable="false"
							className={selectedIndex === i ? 'carouselSelectedImage' : undefined}
							src={src}
							alt={carouselPart.alt}
							onClick={() => setSelectedIndex(i)}
						/>
					);
				})}
			</div>}
			<ImagePopout
				imageRef={currentImageRef}
				changeSlideShowFunction={changeIndex}
				currentSlideShowSrc={currentImageSrc}
				bottomText={<span>{cPart.images[selectedIndex].desc && cPart.images[selectedIndex].desc}</span>}
				maxImages={cPart.images.length}
				imageIndex={selectedIndex}
			/>
		</div>
	);
}
